import { Box, Text } from "@saleor/macaw-ui-next";
import React from "react";

import { SalesAnalyticsData } from "../../types";

interface SalesDashboardProps {
  data: SalesAnalyticsData;
  currency: string;
}

export const SalesDashboard: React.FC<SalesDashboardProps> = ({ data, currency }) => {
  const formatCurrency = (val: number) =>
    `${currency} ${Number(val || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const kpis = [
    { label: "Total Revenue", value: formatCurrency(data.totalRevenue) },
    { label: "Total Orders", value: data.totalOrders.toLocaleString() },
    { label: "Ledger Expenses", value: formatCurrency(data.totalExpenses) },
    { label: "Average Order Value (AOV)", value: formatCurrency(data.averageOrderValue) },
    { label: "Gross Profit", value: formatCurrency(data.grossProfit) },
    { label: "Net Profit", value: formatCurrency(data.netProfit) },
  ];

  return (
    <Box display="flex" flexDirection="column" gap={6}>
      {/* Metric Cards Grid */}
      <Box display="grid" __gridTemplateColumns="repeat(auto-fit, minmax(220px, 1fr))" gap={4}>
        {kpis.map((kpi, idx) => (
          <Box
            key={idx}
            padding={4}
            borderWidth={1}
            borderStyle="solid"
            borderColor="default1"
            borderRadius={3}
            backgroundColor="default1"
            display="flex"
            flexDirection="column"
            gap={1}
          >
            <Text size={2} color="default1" fontWeight="bold">
              {kpi.label}
            </Text>
            <Text size={6} fontWeight="bold">
              {kpi.value}
            </Text>
          </Box>
        ))}
      </Box>

      {/* Sales Trends Breakdown */}
      <Box
        borderWidth={1}
        borderStyle="solid"
        borderColor="default1"
        borderRadius={3}
        backgroundColor="default1"
        padding={4}
        display="flex"
        flexDirection="column"
        gap={3}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Text size={4} fontWeight="bold">
            Sales & Net Profit Trends
          </Text>
          <Text size={2} color="default1">
            {data.trends.length} Data Points
          </Text>
        </Box>

        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              textAlign: "left",
              fontSize: "13px",
            }}
          >
            <thead>
              <tr style={{ borderBottom: "1px solid #eee", backgroundColor: "#fafafa" }}>
                <th style={{ padding: "10px" }}>Date</th>
                <th style={{ padding: "10px", textAlign: "right" }}>Revenue</th>
                <th style={{ padding: "10px", textAlign: "center" }}>Orders</th>
                <th style={{ padding: "10px", textAlign: "right" }}>Expenses</th>
                <th style={{ padding: "10px", textAlign: "right" }}>Net Profit</th>
              </tr>
            </thead>
            <tbody>
              {data.trends.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: "20px", textAlign: "center", color: "#888" }}>
                    No order activity recorded for this period.
                  </td>
                </tr>
              ) : (
                data.trends.map((pt, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "10px", fontWeight: "bold" }}>{pt.date}</td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "right",
                        color: "#2e7d32",
                        fontWeight: "bold",
                      }}
                    >
                      {formatCurrency(pt.revenue)}
                    </td>
                    <td style={{ padding: "10px", textAlign: "center" }}>{pt.ordersCount}</td>
                    <td style={{ padding: "10px", textAlign: "right", color: "#c62828" }}>
                      {formatCurrency(pt.expenses)}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "right",
                        fontWeight: "bold",
                        color: pt.netProfit >= 0 ? "#2e7d32" : "#c62828",
                      }}
                    >
                      {formatCurrency(pt.netProfit)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Box>
    </Box>
  );
};
